import { createSelector } from 'reselect';
import {
  searchFormArrivalDateDateSelector,
  searchFormDurationSelector,
  searchFormPartySizeSelector,
  searchFormPetFriendlySelector,
  searchFormRegionIdSelector,
} from '../../search/form/selectors';
import {
  addMinPriceBasedOnPartySize,
  applyBasicFiltersToDataset,
  getMinPricesForEachDate,
} from '../../../utils/cachedAvail';
import { dateToNum } from '../../../utils/utils';
import { dateToDDMMYYYY, DDMMYYYYToDate } from '../../../utils/date';
import { enGB } from 'date-fns/locale';
import { startOfWeek } from 'date-fns';
import { channelRegionIdsSelector, regionsSelector } from '../regions/selectors';
import { getRegionsByChannelId } from '../regions/utils';
import { minPricesSelector } from './selectors';

// Base Selector
// SSOT takes data for minPrices, parsedDuration, petFriendly, partySize
// filters minPrices based on these values and then adds the minimum price
export const cachedAvailFilteredSelector = createSelector(
  [minPricesSelector, searchFormDurationSelector, searchFormPetFriendlySelector, searchFormPartySizeSelector],
  (minPrices, duration, petFriendly, partySize) => {
    const parsedDuration = parseInt(duration, 10) || 1;
    const basicFiltered = applyBasicFiltersToDataset(minPrices, parsedDuration, petFriendly, partySize);
    return basicFiltered.map((obj) => addMinPriceBasedOnPartySize(obj, partySize));
  },
);

// DatesSlider
// Returns an array of [{date: price}] 'ddmmyyyy': number
// representing the cheapest price for each date
export const minPricesByDateSelector = createSelector([cachedAvailFilteredSelector], (cachedAvailFiltered) => {
  if (!cachedAvailFiltered?.length) return [];

  const priceMap = getMinPricesForEachDate(cachedAvailFiltered);

  return Array.from(priceMap, ([date, price]) => ({
    date,
    price: Math.ceil(price),
  })).sort((a, b) => dateToNum(a.date) - dateToNum(b.date));
});

// AllRegionsSearchResults
// Take the original filtered data set and further filter by arrival date
export const regionsWithMinPriceSelector = createSelector(
  [cachedAvailFilteredSelector, searchFormArrivalDateDateSelector, channelRegionIdsSelector, regionsSelector],
  (cachedAvailFiltered, arrivalDate, channelRegions, regions) => {
    const channelRegionsData = getRegionsByChannelId(regions, channelRegions, 'ukcaravan');
    if (!arrivalDate || !cachedAvailFiltered?.length || !channelRegionsData?.length) return [];

    const formattedDate = dateToDDMMYYYY(new Date(arrivalDate));

    const filteredByArrival = cachedAvailFiltered.filter((holiday) => holiday.date === formattedDate);

    const holidaysWithRegionInfo = filteredByArrival
      .map((holiday) => {
        const region = channelRegionsData.find((r) => String(r.id) === String(holiday.region));
        return {
          ...holiday,
          id: region?.id || null,
          name: region?.name || null,
          desc: region?.desc || null,
          price: holiday.price || null,
        };
      })
      .filter((holiday) => holiday.price != null); // keep only holidays with a price

    const cheapestPerRegionMap = new Map();

    holidaysWithRegionInfo.forEach((holiday) => {
      if (!cheapestPerRegionMap.has(holiday.id)) {
        cheapestPerRegionMap.set(holiday.id, holiday);
      } else {
        const existing = cheapestPerRegionMap.get(holiday.id);
        if (Number(holiday.price) < Number(existing.price)) {
          cheapestPerRegionMap.set(holiday.id, holiday);
        }
      }
    });

    return Array.from(cheapestPerRegionMap.values());
  },
);

// DatePicker
// Returns the cheapest holiday for each date, if multiple returns soonest
export const filteredHolidaysWithCheapestSelector = createSelector(
  [cachedAvailFilteredSelector, searchFormRegionIdSelector],
  (holidays, selectedRegionId) => {
    if (!holidays?.length) return [];

    const filtered =
      selectedRegionId === 'all-regions' ? holidays : holidays.filter((h) => h.region === selectedRegionId);

    const minimumPriceForEachDate = getMinPricesForEachDate(filtered);

    const cheapestPerDate = Array.from(
      filtered
        .filter((h) => Number(h.price) === minimumPriceForEachDate.get(h.date))
        .sort((a, b) => dateToNum(a.date) - dateToNum(b.date)) // earliest date first
        .reduce((map, h) => {
          if (!map.has(h.date)) map.set(h.date, h); // keep only one per date
          return map;
        }, new Map())
        .values(),
    );

    return markCheapestPerWeek(cheapestPerDate);
  },
);

const getWeekStart = (date) => startOfWeek(date, { locale: enGB });

const markCheapestPerWeek = (items) => {
  const groups = items.reduce((acc, item) => {
    const date = DDMMYYYYToDate(item.date);
    const weekStart = getWeekStart(date);

    if (!acc[weekStart]) acc[weekStart] = [];
    acc[weekStart].push(item);

    return acc;
  }, {});

  // For each week, find min price and mark cheapest
  Object.values(groups).forEach((weekItems) => {
    const minPrice = Math.min(...weekItems.map((holiday) => Number(holiday.price)));

    weekItems.forEach((holiday) => {
      holiday.cheapestInWeek = Number(holiday.price) === minPrice;
    });
  });

  return Object.values(groups).flat();
};
